import { green, grey, orange, red } from "@mui/material/colors";
import {
  format,
  formatDistance,
  formatISO,
  getWeek,
  isToday,
  isTomorrow,
  isValid,
  Locale,
  parse,
  parseISO,
  startOfToday,
} from "date-fns";
import { de } from "date-fns/locale";
import { fromZonedTime } from "date-fns-tz";
import StatusFilled from "icons/StatusFilled.svg";
import StatusUnfilled from "icons/StatusUnfilled.svg";
import _isNull from "lodash/isNull";
import _isUndefined from "lodash/isUndefined";
import { TranslateFunction } from "react-admin";

import { EventRecordType, EventStatusEnum, NonStaffEventRecordType } from "../pages/Event/Event.types";

export type StringOrDateType = string | Date;

export const nonBreakingSpace = "\u00A0";

function parseDay(day: string, format: string): Date {
  return parse(day, format, new Date());
}

export function deFormat(day: Date | string, givenFormat = "dd. MMMM yyyy"): string {
  return format(day, givenFormat, { locale: de });
}

export function formatBetweenDayFormats(fromFormat: string, toFormat: string, options?: { locale: Locale }) {
  return (inputDay: StringOrDateType) => {
    let day: string | Date = inputDay;
    if (typeof inputDay === "string") {
      day = parseDay(day as string, fromFormat);
    }

    if (!isValid(day)) return inputDay as string;

    return format(day, toFormat, options);
  };
}

export const dayGermanFormat = formatBetweenDayFormats("yyyy-MM-dd", "dd.MM.yyy");
export const dayWithMonthGermanFormat = formatBetweenDayFormats("yyyy-MM-dd", "dd. MMMM yyyy", { locale: de });
export const dayMachineFormat = formatBetweenDayFormats("dd.MM.yyy", "yyyy-MM-dd");

export const todayMachineFormat = dayMachineFormat(startOfToday());

export const parseDatetime = (datetime: StringOrDateType): Date => {
  if (typeof datetime === "string") {
    return parseISO(datetime);
  } else {
    return datetime;
  }
};

export const datetimeDistance = (inputDatetime: StringOrDateType, from?: Date): string => {
  const fromDate = from || new Date();
  return formatDistance(parseDatetime(inputDatetime), fromDate, {
    addSuffix: true,
    locale: de,
  });
};

export const datetimeGermanFormat = (inputDatetime: StringOrDateType | undefined | null): string | null | undefined => {
  if (_isUndefined(inputDatetime) || _isNull(inputDatetime)) {
    return inputDatetime;
  }
  return format(parseDatetime(inputDatetime), "dd.MM.yyyy HH:mm");
};

export const timeFromDatetime = (inputDatetime: StringOrDateType): string =>
  format(parseDatetime(inputDatetime), "HH:mm");

export const dateFromDatetime = (inputDatetime: StringOrDateType): string =>
  format(parseDatetime(inputDatetime), "dd.MM.yyyy");

export const shortDateFromDatetime = (inputDatetime: StringOrDateType): string =>
  format(parseDatetime(inputDatetime), "dd.MM.");

export const datetimeSpanGermanFormat = (startDatetime: StringOrDateType, endDatetime: StringOrDateType): string => {
  const startDate = dateFromDatetime(startDatetime);
  const endDate = dateFromDatetime(endDatetime);
  const startTime = timeFromDatetime(startDatetime);
  const endTime = timeFromDatetime(endDatetime);

  if (startDate === endDate) {
    return `${startDate} ${startTime} - ${endTime}`;
  } else {
    return `${startDate} ${startTime} - ${endDate} ${endTime}`;
  }
};

// Default times used when an event date has no explicit time ("Termin ohne Uhrzeit").
// Kept in one place so every write path assembles start/end datetimes identically.
export const DEFAULT_START_TIME = "00:00";
export const DEFAULT_END_TIME = "23:59";

export const buildEventDateDatetimes = (
  day: string,
  start_time?: string | null,
  end_time?: string | null,
): { start_datetime: string; end_datetime: string } => {
  const utcString = (time: string) => formatISO(fromZonedTime(`${day} ${time}`, "Europe/Berlin"));
  return {
    start_datetime: utcString(start_time || DEFAULT_START_TIME),
    end_datetime: utcString(end_time || DEFAULT_END_TIME),
  };
};

export function formatDateNaturalName(date: StringOrDateType, translate: TranslateFunction): string {
  if (typeof date == "string") {
    date = new Date(date);
  }

  if (isToday(date)) {
    return translate("today");
  } else if (isTomorrow(date)) {
    return translate("tomorrow");
  }
  return dateFromDatetime(date);
}

export function formatEventDateSpan(
  event: EventRecordType | NonStaffEventRecordType,
  noDates: string | null = null,
): string | null {
  let firstDay: Date;
  let lastDay: Date;

  if (event.first_day && event.last_day) {
    firstDay = parse(event.first_day, "yyyy-MM-dd", new Date());
    lastDay = parse(event.last_day, "yyyy-MM-dd", new Date());
  } else if (event.dates && event.dates.length) {
    firstDay = parse(event.dates[0].day, "yyyy-MM-dd", new Date());
    lastDay =
      event.dates.length > 1 ? parse(event.dates[event.dates.length - 1].day, "yyyy-MM-dd", new Date()) : firstDay;
  } else {
    return noDates;
  }

  if (firstDay.getTime() === lastDay.getTime()) {
    return deFormat(firstDay, "dd. MMMM yyyy");
  }

  if (firstDay.getFullYear() !== lastDay.getFullYear()) {
    return `${deFormat(firstDay, "dd. MMM yyyy")} - ${deFormat(lastDay, "dd. MMM yyyy")}`;
  } else if (firstDay.getMonth() !== lastDay.getMonth()) {
    return `${deFormat(firstDay, "dd. MMM")} - ${deFormat(lastDay, "dd. MMM yyyy")}`;
  } else {
    return `${deFormat(firstDay, "dd.")} - ${deFormat(lastDay, "dd. MMMM yyyy")}`;
  }
}

export const dateFieldFormatOptions = {
  day: "2-digit" as const,
  month: "2-digit" as const,
  year: "numeric" as const,
};

export function pluralizeResource(resourceName: string): string {
  const pluralizedMapping: { [k: string]: string } = {
    category: "categories",
    responsibility: "responsibilities",
    user_list_data: "user_list_data",
    external_template: "external_email_templates",
  };
  return pluralizedMapping[resourceName] || `${resourceName}s`;
}

export function formatAmount(amount: number, formatOpts?: Intl.NumberFormatOptions): string {
  formatOpts = {
    ...{ minimumFractionDigits: 2, maximumFractionDigits: 2 },
    ...formatOpts,
  };
  return amount.toLocaleString(undefined, formatOpts);
}

export function formatAmountWithCurrency(amount: number, currency = "€") {
  if (amount == 0) {
    return "—";
  }

  return `${formatAmount(amount)}${nonBreakingSpace}${currency}`;
}

export function formatAmountWithoutDecimals(amount: number): string {
  return formatAmount(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export const formatRatio = (ratio: number): string => {
  return `${ratio.toFixed(2)}${nonBreakingSpace}%`;
};

export function getClearedAmountColor(invoicedAmount: number, bookingAmount: number): string {
  if (invoicedAmount === bookingAmount) {
    return green[500];
  } else if (invoicedAmount > bookingAmount) {
    return orange[500];
  }

  return "#000";
}

export function getEventStatusColor(status: EventStatusEnum): string {
  const color = {
    [EventStatusEnum.Draft]: grey[600],
    [EventStatusEnum.Offered]: green[500],
    [EventStatusEnum.TakesPlace]: green[500],
    [EventStatusEnum.Finished]: grey[600],
    [EventStatusEnum.Canceled]: red[500],
  }[status];

  return color || grey[600];
}

export function getEventStatusIcon(status: EventStatusEnum) {
  const icon = {
    [EventStatusEnum.Draft]: StatusUnfilled,
    [EventStatusEnum.Offered]: StatusUnfilled,
    [EventStatusEnum.TakesPlace]: StatusFilled,
    [EventStatusEnum.Finished]: StatusFilled,
    [EventStatusEnum.Canceled]: StatusFilled,
  }[status];

  return icon || StatusFilled;
}

export function getBookingStatusColor(status: string): string {
  const color = {
    VALID: green[500],
    WAITING: orange[500],
    CANCELED: red[500],
    REBOOKED: red[500],
  }[status];

  return color || grey[500];
}

export function timeWithSecondsFromDate(date?: Date): string {
  if (typeof date === "undefined") {
    return "";
  }

  return format(date, "HH:mm:ss");
}

export function uppercaseFirstLetter(str: string): string {
  if (!str) {
    return "";
  } else {
    return `${str[0].toUpperCase()}${str.slice(1)}`;
  }
}

export function getWeekNumber(date: Date): number {
  return getWeek(date, { weekStartsOn: 1, firstWeekContainsDate: 4 });
}

export const shortenAmount = (
  amount: number,
  withDecimals: boolean | { millions?: boolean; thousands?: boolean; hundreds?: boolean } = { millions: true },
): string => {
  const getDecimals = (
    type: "m" | "k" | "h",
    withDecimals?: boolean | { millions?: boolean; thousands?: boolean; hundreds?: boolean },
  ): number => {
    if (withDecimals) {
      if (
        (typeof withDecimals == "boolean" && withDecimals) ||
        (type == "m" && withDecimals.millions) ||
        (type == "k" && withDecimals.thousands) ||
        (type == "h" && withDecimals.hundreds)
      ) {
        return 2;
      }
    }
    return 0;
  };

  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(getDecimals("m", withDecimals))}m`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(getDecimals("k", withDecimals))}k`;
  }
  return amount.toFixed(getDecimals("h", withDecimals));
};

export const slugPersonName = (firstName: string, lastName: string) => {
  return firstName.slice(0, 1).toUpperCase() + lastName.slice(0, 1).toUpperCase();
};

const getHashOfString = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(hash);
  return hash;
};

const normalizeHash = (hash: number, min: number, max: number) => {
  return Math.floor((hash % (max - min)) + min);
};

const hRange = [0, 360];
const sRange = [65, 95];
const lRange = [30, 50];

export const generateHslColor = (name: string): string => {
  const hash = getHashOfString(name);
  const h = normalizeHash(hash, hRange[0], hRange[1]);
  const s = normalizeHash(hash, sRange[0], sRange[1]);
  const l = normalizeHash(hash, lRange[0], lRange[1]);
  return `hsl(${h}, ${s}%, ${l}%)`;
};

export const getReadableColorByBackground = (
  backgroundColor: string,
  defaultColors = { light: "#FFFFFF", dark: "#000000" },
) => {
  const colorValue = parseInt(backgroundColor.replace("#", ""), 16);
  if (colorValue > 14000000) {
    return defaultColors.dark;
  }
  return defaultColors.light;
};

export const parseTimeToToday = (timeStr: string) => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

export function formatBytes(bytes: number) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + " " + sizes[i];
}
